/**
 * Tracking service - public API for analytics tracking
 *
 * Things not captured:
 * - resume number of pages
 */

import { getAuthState } from '@/stores/auth-store'
import {
  registerProvider,
  trackAll,
  identifyAll,
  resetAll,
  setGroupAll,
  identifyGroupAll,
  clearGroupAll,
} from './track/registry'
import { cookieConsent } from './consent'
import { createConsoleProvider } from './track/providers/console'
import { createMixpanelProvider } from './track/providers/mixpanel'
import { createAmplitudeProvider } from './track/providers/amplitude'
import { createGA4Provider } from './track/providers/ga4'
// import { createMetaPixelProvider } from './track/providers/meta-pixel'

// Re-export types for external consumers
export type {
  Status,
  Page,
  Section,
  ElementType,
  ElementId,
  Source,
  Envelope,
  TrackEvent,
  FileUploadEvent,
  FileUploadedEvent,
  FileDownloadEvent,
  FileDownloadedEvent,
  FieldUpdateEvent,
  UIClickEvent,
  AuthEvent,
  QuotaEvent,
  PageNavigationEvent,
  ClientErrorEvent,
  APIErrorEvent,
  TrackingProvider,
  UserProperties,
  GroupProperties,
} from './track/types'

import type {
  Page,
  Section,
  Envelope,
  TrackEvent,
  FileUploadEvent,
  FileUploadedEvent,
  FileDownloadEvent,
  FileDownloadedEvent,
  UIClickEvent,
  AuthEvent,
  QuotaEvent,
} from './track/types'

type IdentifyUserOptions = {
  id?: string | null
  email?: string | null
  name?: string | null
}

type IdentifyAgencyOptions = {
  id?: string | null
  name?: string | null
}

const isProductionStage = import.meta.env.VITE_STAGE === 'production'
const AGENCY_GROUP_KEY = 'agency'

// Initialize providers based on environment — but only after the visitor
// grants analytics consent (cookie banner). Until then every Track call
// is a no-op and no analytics cookies are set. Consent is persisted, so
// returning visitors register providers immediately at load.
let providersRegistered = false
function registerAnalyticsProviders() {
  if (providersRegistered || typeof window === 'undefined') return
  providersRegistered = true
  if (isProductionStage) {
    registerProvider(createMixpanelProvider())
    registerProvider(createAmplitudeProvider())
    registerProvider(createGA4Provider())
    // registerProvider(createMetaPixelProvider())
  } else {
    registerProvider(createConsoleProvider())
  }
}

if (typeof window !== 'undefined') {
  cookieConsent.on(consent => {
    if (consent === 'granted') registerAnalyticsProviders()
  })
}

const basePages = {
  '/': 'home',
  '/about': 'about',
  '/contact': 'contact',
  '/terms': 'terms',
  '/faq': 'faq',
  '/privacy': 'privacy',
  '/trial': 'trial',
  '/signup': 'signup',
  '/signin': 'signin',
  '/forgot-password': 'forgot_password',
  '/reset-password': 'reset_password',
  '/confirm-email': 'confirm_email',
  '/verify-email': 'verify_email',
  '/onboarding': 'onboarding',
  '/start': 'start',
} as const

const agencyPages = {
  '/workspace': 'workspace',
  '/customize': 'customize',
  '/profile': 'profile',
  '/agency': 'agency',
  '/team': 'agency',
  '/billing': 'billing',
  '/onboarding': 'onboarding',
} as const

function matchPageAndAgency(pathname: string = window.location.pathname): {
  page: Page
  agency_id?: string
} {
  if (pathname in basePages) {
    return {
      page: basePages[pathname as keyof typeof basePages]!,
      agency_id: undefined,
    }
  }
  const parts = pathname.split('/')
  const agency_id = parts.shift()
  const subPath = '/' + (parts.pop() ?? '')
  if (subPath in agencyPages) {
    return {
      page: agencyPages[subPath as keyof typeof agencyPages]!,
      agency_id,
    }
  }
  return { page: 'unknown', agency_id }
}

function ensurePageContext(): {
  agency_id?: string
  page: Page
  url: string
  referrer?: string
} {
  const url = window.location.href
  const { page, agency_id } = matchPageAndAgency()
  const referrer = document.referrer
  return {
    agency_id,
    page,
    url,
    referrer,
  }
}

function ensureUserContext(): {
  user_id?: string
  user_active?: boolean
  email_verified?: boolean
} {
  const state = getAuthState()
  const user_id = state.user?.id
  const user_active = state.user != null ? true : undefined
  const email_verified = state.user?.emailVerified
  return {
    user_id,
    user_active,
    email_verified,
  }
}

async function track(e: TrackEvent) {
  const { user_id, user_active, email_verified } = ensureUserContext()
  const { agency_id, page, url, referrer } = ensurePageContext()
  const env: Envelope = {
    version: 1,
    source: 'web',
    agency_id,
    user_id,
    user_active,
    email_verified,
    page,
    url,
    referrer,
  }
  await trackAll(e.type, { ...env, ...e })
}

async function identifyUser(options?: IdentifyUserOptions) {
  const state = getAuthState()
  const userFromState = state.user
  const user_id = options?.id ?? userFromState?.id
  const email = options?.email ?? userFromState?.email
  const name =
    options?.name ?? userFromState?.fullName ?? email ?? 'ResumeHeaders User'

  if (!user_id) {
    await resetAll()
    return
  }

  await identifyAll(user_id, { name, email: email ?? undefined })
}

async function identifyAgency(options?: IdentifyAgencyOptions) {
  const agencyFromPage =
    typeof window !== 'undefined' ? ensurePageContext().agency_id : undefined
  const agency_id = options?.id ?? agencyFromPage
  const agency_name = options?.name ?? agency_id

  if (!agency_id) {
    await clearGroupAll(AGENCY_GROUP_KEY)
    return
  }

  await setGroupAll(AGENCY_GROUP_KEY, agency_id)
  await identifyGroupAll(AGENCY_GROUP_KEY, agency_id, {
    name: agency_name,
  })
}

function uploaded(p: {
  status: FileUploadedEvent['status']
  error_code?: string
  file_type?: string
  file_size?: number
}) {
  track({
    type: 'file_uploaded',
    ...p,
  })
}

function upload(p: { trigger?: FileUploadEvent['trigger']; section: Section }) {
  track({
    type: 'file_upload',
    ...p,
  })
}

function download(p: {
  trigger: FileDownloadEvent['trigger']
  section: Section
}) {
  track({
    type: 'file_download',
    ...p,
  })
}

function downloaded(p: {
  status: FileDownloadedEvent['status']
  error_code?: string
  file_type?: string
  file_size?: number
}) {
  track({
    type: 'file_downloaded',
    ...p,
  })
}

function fieldInput(p: {
  section: Section
  field_name: string
  previous_value?: string
  new_value: string
}) {
  track({
    type: 'field_update',
    ...p,
  })
}

function click(p: {
  section: Section
  element_type: UIClickEvent['element_type']
  click_action: UIClickEvent['click_action']
  element_id: UIClickEvent['element_id']
  element_value?: string
}) {
  track({
    type: 'ui_click',
    ...p,
  })
}

function auth(p: {
  action: AuthEvent['action']
  user_id?: string
  message?: string
}) {
  track({
    type: 'auth',
    ...p,
  })
}

function quota(p: {
  limit_type: QuotaEvent['limit_type']
  current_usage: number
  plan_limit: number
}) {
  track({
    type: 'quota_exceeded',
    ...p,
  })
}

function getTimestamp(): number {
  return new Date().getTime()
}

let current_page = null as {
  start_time: number
  page: Page
  url: string
} | null

function navigation() {
  const { start_time, page, url } = current_page ?? {}
  const to_url = window.location.pathname
  current_page = {
    start_time: getTimestamp(),
    page: matchPageAndAgency(to_url).page,
    url: to_url,
  }
  // TODO discount idle/away time
  if (start_time && page && url) {
    const previous_page_duration = current_page.start_time - start_time
    track({
      type: 'page_navigation',
      from: page,
      from_url: url,
      to: current_page.page,
      to_url: current_page.url,
      previous_page_duration,
    })
  } else {
    track({
      type: 'page_navigation',
      to: current_page.page,
      to_url: current_page.url,
    })
  }
}

function clientError(p: { error_code?: string; error_message: string }) {
  track({
    type: 'client_error',
    ...p,
  })
}

function apiError(p: { error_code?: string; error_message: string }) {
  track({
    type: 'api_error',
    source: 'api',
    ...p,
  })
}

if (typeof window !== 'undefined') {
  void identifyUser()
  void identifyAgency()
}

export const Track = {
  upload,
  uploaded,
  download,
  downloaded,
  fieldInput,
  click,
  auth,
  quota,
  navigation,
  clientError,
  apiError,
  identify: identifyUser,
  setAgency: identifyAgency,
}
